import { useEffect, useState } from "react";
import useCurrentUser from "../../hooks/useCurrentUser";

import { EProjectStatus, IProject } from "../../data_models/project";

import useMyProjects from "../../hooks/useMyProjects";
import { IFreelancer, RestrictedFreelancerRole } from "../../data_models/freelancer";
import { OutlinedDropdownMenu } from "../common/DropdownMenu";
import { ProjectCard } from "../../pages/MyProjectsPage";
import useGlobalLoading from "../../hooks/useGlobalLoading";
import useProjects from "../../hooks/useProjects";
import useRouting from "../../hooks/useRouting";


const FreelancerMyProjects = () => {

    const filterOptions = [
        'All',
        'Ongoing',
        'Completed',
    ]

    const tabs = [
        'As Talent',
        'As Project Manager',
    ]

    const sortOptions = [
        'Newest First',
        'Oldest First',
        'Status (A-Z)',
        'Status (Z-A)',
        'Project Title (A-Z)',
        'Project Title (Z-A)',
    ]

    const [activeFilter, setActiveFilter] = useState(filterOptions[0]);
    const [activeTab, setActiveTab] = useState(tabs[0]);
    const [activeSort, setActiveSort] = useState(sortOptions[0]);
    const { currentUser } = useCurrentUser();
    const { setLoading } = useGlobalLoading();

    const [projectsAsPm, setProjectsAsPm] = useState<IProject[]>([]);
    const [projectsAsFreelancer, setProjectsAsFreelancer] = useState<IProject[]>([]);
    const [filteredProjectsAsPm, setFilteredProjectsAsPm] = useState<IProject[]>([]);
    const [filteredProjectsAsFreelancer, setFilteredProjectsAsFreelancer] = useState<IProject[]>([]);
    const [activeProjects, setActiveProjects] = useState<IProject[]>([]);

    const { fetchMyProjectsAsPm, fetchProjectsByFilter } = useProjects();
    const { goToProjectDetails } = useRouting();

    useEffect(() => {
        if (!currentUser) return;
        const fetchProjects = async () => {
            setLoading(true);
            const filterFreelancer = {
                assignedFreelancers: { $elemMatch: { freelancerId: currentUser._id } }
            }
            await Promise.all([
                fetchMyProjectsAsPm(1, 99999).then((projects) => {
                    setProjectsAsPm(projects);
                    setFilteredProjectsAsPm(projects);
                }),
                fetchProjectsByFilter(filterFreelancer, 1, 99999).then((projects) => {
                    setProjectsAsFreelancer(projects.map((project) => project.project));
                    setFilteredProjectsAsFreelancer(projects.map((project) => project.project));
                }),
            ]);
            setLoading(false);
        };

        fetchProjects();
    }, []);

    useEffect(() => {
        getFilteredProjects();
    }, [projectsAsFreelancer,
        projectsAsPm,
        activeFilter,
        activeSort,
    ]);

    useEffect(() => {
        setActiveProjects(activeTab === 'As Talent' ? filteredProjectsAsFreelancer : filteredProjectsAsPm);
    }, [activeTab, filteredProjectsAsFreelancer, filteredProjectsAsPm]);

    const getFilteredProjects = () => {
        const filteredProjectsAsPm: IProject[] = [];
        const filteredProjectsAsFreelancer: IProject[] = [];
        if (activeFilter === 'All') {
            filteredProjectsAsPm.push(
                ...projectsAsPm);
            filteredProjectsAsFreelancer.push(
                ...projectsAsFreelancer);
        } else if (activeFilter === 'Ongoing') {
            filteredProjectsAsPm.push(
                ...projectsAsPm.filter((project) => (project.status || Infinity) < EProjectStatus.COMPLETED));
            filteredProjectsAsFreelancer.push(
                ...projectsAsFreelancer.filter((project) => (project.status || Infinity) < EProjectStatus.COMPLETED));
        } else if (activeFilter === 'Completed') {
            filteredProjectsAsPm.push(
                ...projectsAsPm.filter((project) => project.status === EProjectStatus.COMPLETED));
            filteredProjectsAsFreelancer.push(
                ...projectsAsFreelancer.filter((project) => project.status === EProjectStatus.COMPLETED));
        }

        // Apply sorting
        const sortedProjectsAsPm = [...filteredProjectsAsPm];
        const sortedProjectsAsFreelancer = [...filteredProjectsAsFreelancer];

        switch (activeSort) {
            case 'Newest First':
                sortedProjectsAsPm.sort((a, b) => new Date(b._createdAt!).getTime() - new Date(a._createdAt!).getTime());
                sortedProjectsAsFreelancer.sort((a, b) => new Date(b._createdAt!).getTime() - new Date(a._createdAt!).getTime());
                break;
            case 'Oldest First':
                sortedProjectsAsPm.sort((a, b) => new Date(a._createdAt!).getTime() - new Date(b._createdAt!).getTime());
                sortedProjectsAsFreelancer.sort((a, b) => new Date(a._createdAt!).getTime() - new Date(b._createdAt!).getTime());
                break;
            case 'Status (A-Z)':
                sortedProjectsAsPm.sort((a, b) => (a.status || 0) - (b.status || 0));
                sortedProjectsAsFreelancer.sort((a, b) => (a.status || 0) - (b.status || 0));
                break;
            case 'Status (Z-A)':
                sortedProjectsAsPm.sort((a, b) => (b.status || 0) - (a.status || 0));
                sortedProjectsAsFreelancer.sort((a, b) => (b.status || 0) - (a.status || 0));
                break;
            case 'Project Title (A-Z)':
                sortedProjectsAsPm.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                sortedProjectsAsFreelancer.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                break;
            case 'Project Title (Z-A)':
                sortedProjectsAsPm.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
                sortedProjectsAsFreelancer.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
                break;
        }

        setFilteredProjectsAsPm(sortedProjectsAsPm);
        setFilteredProjectsAsFreelancer(sortedProjectsAsFreelancer);

        // set active tab to the one with more ongoingprojects
        // const tmpFilteredPmOngoing = [...projectsAsPm.filter((project) => (project.status || Infinity) < EProjectStatus.COMPLETED)]
        //     .sort((a, b) => new Date(b._createdAt!).getTime() - new Date(a._createdAt!).getTime()); // youngest first
        // const tmpFilteredFreelancerOngoing = [...projectsAsFreelancer.filter((project) => (project.status || Infinity) < EProjectStatus.COMPLETED)]
        //     .sort((a, b) => new Date(b._createdAt!).getTime() - new Date(a._createdAt!).getTime()); // youngest first
        // if (tmpFilteredPmOngoing.length > tmpFilteredFreelancerOngoing.length) {
        //     setActiveTab(tabs[1]);
        // } else {
        //     setActiveTab(tabs[0]);
        // }

        // setFilteredProjectsAsPm(filteredProjectsAsPm);
        // setFilteredProjectsAsFreelancer(filteredProjectsAsFreelancer);
    }

    return (
        <div className="p-4 max-w-7xl mx-auto">

            {/* Header section */}
            <div className="flex flex-col md:flex-row justify-between md:items-center">
                <h2 className="text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">Projects</h2>
                <div className="flex flex-row space-x-2 mt-4 md:mt-0">
                    <div className="min-w-36">
                        <OutlinedDropdownMenu
                            options={filterOptions}
                            onSelect={(option) => setActiveFilter(option)}
                            defaultValue={activeFilter}
                        />
                    </div>
                    <div className="min-w-36">
                        <OutlinedDropdownMenu
                            options={sortOptions}
                            onSelect={(option) => setActiveSort(option)}
                            defaultValue={activeSort}
                        />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            {
                (currentUser as IFreelancer).restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER) &&
                <div className="mt-4 px-3 py-2 bg-white rounded-md w-full">
                    <div className="flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`text-left rounded-md px-2 py-2 text-sm font-medium transition-all duration-300
                                                ${activeTab === tab
                                        ? 'bg-primary-500 text-white'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                                    }`}
                            >
                                {tab}
                                {/* Badge for count */}
                                <span className="ml-2 border border-primary bg-primary-100 text-primary-800 rounded-full px-3 py-1">
                                    {tab === 'As Talent'
                                        ? projectsAsFreelancer.length
                                        : projectsAsPm.length
                                    }
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            }

            {/* Projects section */}
            {
                activeProjects.length === 0
                    ? <div className={`mt-10 flex flex-col items-center space-y-4`}>
                        <img src="https://storage.googleapis.com/public-platform-assets/images/no_data.svg" alt="No projects" />
                        <p className="body1">No projects found</p>
                    </div>
                    : <div className="mt-4 sm:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeProjects.map((project) => (
                            <ProjectCard
                                key={project._id}
                                project={project}
                                className="hover:scale-105 cursor-pointer transition-all duration-300"
                                onClick={() => {
                                    goToProjectDetails(project._id!);
                                }}
                            />
                        ))}
                    </div>
            }

        </div>
    );
};

export default FreelancerMyProjects;